import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Button } from "@common";

// ─── modal ────────────────────────────────────────────────────────────────────

// Identity for one editable row, unique WITHIN this cell.
//
// It cannot be `originalDate`: a cell is every payment landing on one day, so
// two different schedules in it necessarily share that date. Keying on it
// collapsed both rows onto one state entry — each showed the other's amount and
// saving wrote one value to both. `scheduleId` separates them, and the row index
// is appended so the id holds up whatever the caller passes (a statement payment
// has neither field and supplies its own `key`).
const rowKey = (o, i) => o.key ?? `${o.scheduleId ?? "occ"}|${o.originalDate ?? ""}|${i}`;

// Money for an <input>: plain digits and at most one decimal point, no currency
// symbol or grouping, so what is typed round-trips through Number().
const toAmountInput = (v) => (v == null || v === "" ? "" : String(Math.abs(Number(v))));

// Compare in whole cents — the stored value is a 2-decimal DecimalField, so
// comparing floats would report a change for a value the API cannot even hold.
const cents = (v) => Math.round(Number(v) * 100);

/**
 * Edit one or more payments that land on the same cell: their date, and — where
 * the host allows it — their amount.
 *
 * How the edit is PERSISTED depends on the payment and is decided by the caller:
 * a scheduled occurrence gets a one-off exception carrying the new date and/or
 * amount (the recurring rule is untouched), while a statement payment has no
 * exception model and has its date edited directly in the statement's splits.
 *
 * `allowAmountEdit` is the HOST's decision, not a property of the payment. The
 * clinic grid (ClinicCashFlow) allows it; the Dens N Dente entity grid does not, and
 * is date-only. A statement payment is never amount-editable regardless: its
 * `splits` must add up to the statement balance, so one split cannot move alone.
 *
 * SKIPPING is the third edit, and the only one that removes a payment from the
 * grid. It persists as an exception with a NULL `new_date` — the series itself
 * is untouched, so every other occurrence still falls where the rule puts it.
 * Deliberately not called "delete": nothing is deleted, the exception row IS
 * the record of the decision, and it is reversed from the All Payments tab.
 */
export default function MoveOccurrenceModal({
  isOpen,
  onClose,
  cell,
  onSave,
  saving = false,
  allowAmountEdit = true,
  // Statement payments have no exception model, so they are never skippable
  // regardless — see `canSkip`. This only withholds it from the SCHEDULED ones.
  allowSkip = true,
  // Stacking order. Defaults to the app's normal modal layer; a host that opens
  // this ON TOP of another dialog (the confirm-cleared review) raises it, so the
  // two do not depend on which portal happened to mount last.
  zClass = "z-50",
  // Open the whole SERIES for editing, given the row's occurrence. Everything
  // else in this modal edits ONE occurrence; this is the escape hatch to the
  // schedule behind it, so the recurring amount or interval can be corrected
  // without a trip to the All Payments tab. Omitted by a host that cannot edit
  // schedules, and the link goes with it.
  onEditSchedule,
}) {
  // Every row carries its own id, so nothing downstream has to re-derive it and
  // risk deriving it differently.
  const rows = (cell?.occurrences ?? []).map((o, i) => ({ id: rowKey(o, i), o }));
  const canEditAmount = (o) => allowAmountEdit && o.kind !== "statement";
  const anyAmountEditable = rows.some(({ o }) => canEditAmount(o));

  // A skip is stored as an exception with a null `new_date`, and a statement
  // payment has no exception model at all — its date lives in the statement's
  // own `splits`, which must still add up to the balance. So there is nothing
  // to write a skip to, and the button is withheld rather than shown failing.
  //
  // A CONFIRMED clearance also withholds it: somebody checked the bank and
  // recorded that this money left, so "it never happened" is not a claim the
  // grid may make. The backend refuses it too (see the exception serializer) —
  // this is the same rule stated where the user can act on it.
  const canSkip = (o) => allowSkip && o.kind !== "statement" && !o.decision;

  // { [row.id]: { date, amount, skip, reason } } — `amount` is the raw input
  // string, `reason` the note recorded on a skip.
  const [edits, setEdits] = useState({});

  useEffect(() => {
    if (isOpen) {
      const init = {};
      rows.forEach(({ id, o }) => {
        init[id] = {
          date: o.currentDate,
          amount: toAmountInput(o.amount),
          skip: false,
          reason: "",
        };
      });
      setEdits(init);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, cell]);

  function fmtMoney(v) {
    if (v == null) return "";
    return `$${Math.round(Math.abs(Number(v))).toLocaleString("en-CA")}`;
  }

  const editFor = ({ id, o }) =>
    edits[id] ?? {
      date: o.currentDate,
      amount: toAmountInput(o.amount),
      skip: false,
      reason: "",
    };
  const setEdit = (row, patch) =>
    setEdits((prev) => ({ ...prev, [row.id]: { ...editFor(row), ...patch } }));

  // An amount is invalid only once it has been touched into something unusable —
  // blank counts, because a row with no amount cannot be saved.
  const amountError = (row) => {
    if (!canEditAmount(row.o)) return null;
    // A skipped row is not being priced, so its amount box is gone and whatever
    // was typed there before is irrelevant. Validating it anyway would make a
    // row with a cleared amount field unsaveable for a field it no longer shows.
    if (editFor(row).skip) return null;
    const raw = editFor(row).amount;
    if (raw === "" || raw == null) return "Enter an amount.";
    const n = Number(raw);
    if (!Number.isFinite(n)) return "Enter a valid amount.";
    if (n <= 0) return "Amount must be greater than zero.";
    return null;
  };

  const hasErrors = rows.some((row) => amountError(row));
  const anySkipped = rows.some((row) => editFor(row).skip);
  function handleSave() {
    if (hasErrors) return;
    // Only submit rows that actually changed. `newAmount` is null when the
    // amount was left alone, so the caller can omit it from the payload and
    // leave any existing override — or the schedule's own amount — in place.
    const changed = rows
      .map((row) => {
        const { o } = row;
        const { date, amount, skip, reason } = editFor(row);
        // A skip SUPERSEDES the date and amount on the same row: the occurrence
        // is not happening, so where it would have landed and what it would have
        // cost are both moot. Sending them anyway would have the caller write a
        // move and a skip for one occurrence, and the two disagree.
        if (skip && canSkip(o)) {
          return { occurrence: o, newDate: null, newAmount: null, skip: true, reason };
        }
        const newDate = date && date !== o.currentDate ? date : null;
        const newAmount =
          canEditAmount(o) && amount !== "" && cents(amount) !== cents(o.amount)
            ? Number(amount)
            : null;
        return { occurrence: o, newDate, newAmount, skip: false, reason: "" };
      })
      // A skipped row is ALWAYS a change: a skipped occurrence is absent from
      // the grid, so one cannot already have been in this cell.
      .filter(({ newDate, newAmount, skip }) => skip || newDate || newAmount != null);
    onSave?.(changed);
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className={`relative ${zClass}`} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="flex w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-start justify-between border-b border-palette-gray-divider px-6 py-5">
                  <div>
                    <Dialog.Title
                      as="h3"
                      className="text-xl font-semibold text-palette-gray-primary"
                    >
                      {anyAmountEditable ? "Edit payment" : "Move payment date"}
                    </Dialog.Title>
                    <p className="mt-0.5 text-sm text-palette-gray-secondary">
                      {cell?.code} — this payment only
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-palette-gray-divider text-palette-gray-secondary transition-colors hover:bg-palette-gray-subtle"
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>

                {/* Body */}
                <div className="space-y-5 px-6 py-6">
                  {rows.map((row) => {
                    const { o } = row;
                    const edit = editFor(row);
                    const skipped = !!edit.skip;
                    const skippable = canSkip(o);
                    // The amount box is withheld from a skipped row: it is not
                    // being priced any more.
                    const amountEditable = canEditAmount(o) && !skipped;
                    const error = amountError(row);
                    // A recorded human decision that this cleared the bank —
                    // the reason `canSkip` refuses it. Told apart from the
                    // matcher's own guess, which only warrants a warning.
                    const confirmedCleared = o.kind !== "statement" && !!o.decision;
                    const guessedCleared = o.kind !== "statement" && o.cleared && !o.decision;
                    return (
                      <div key={row.id}>
                        <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
                          <span
                            className={`min-w-0 truncate font-medium ${
                              skipped
                                ? "text-palette-gray-placeholder line-through"
                                : "text-palette-gray-heading"
                            }`}
                          >
                            {o.label}
                          </span>
                          <div className="flex shrink-0 items-center gap-3">
                            <span
                              className={`tabular-nums ${
                                skipped
                                  ? "text-palette-gray-placeholder line-through"
                                  : "text-palette-gray-secondary"
                              }`}
                            >
                              {fmtMoney(o.amount)}
                            </span>
                            {/* Per row, not in the footer: a cell is every
                                payment landing on one day, so a single footer
                                button could not say which of them it meant. */}
                            {skippable && (
                              <button
                                type="button"
                                onClick={() => setEdit(row, { skip: !skipped })}
                                className={`rounded-lg border px-2 py-1 text-xs font-medium transition-colors ${
                                  skipped
                                    ? "border-palette-gray-divider text-palette-gray-secondary hover:bg-palette-gray-subtle"
                                    : "border-palette-red-border text-palette-red-text hover:bg-palette-red-soft"
                                }`}
                              >
                                {skipped ? "Undo" : "Skip"}
                              </button>
                            )}
                          </div>
                        </div>
                        {/* A skipped row SWAPS its controls for a reason box
                            rather than grey them out: the date and amount are
                            moot once the payment is not happening, and a
                            disabled field invites a click that does nothing —
                            the same reasoning the amount box already follows.
                            The reason lands on the exception's `notes`, because
                            a payment that vanished off the grid with no
                            explanation is what confuses the next reader. */}
                        {skipped ? (
                          <input
                            type="text"
                            value={edit.reason ?? ""}
                            onChange={(e) => setEdit(row, { reason: e.target.value })}
                            placeholder="Reason (optional) — e.g. paid by cheque last week"
                            className="w-full rounded-xl border border-palette-gray-divider bg-palette-gray-subtle px-3.5 py-2.5 text-sm text-palette-gray-primary transition-colors focus:border-palette-indigo-ring focus:outline-none focus:ring-2 focus:ring-palette-indigo-border"
                          />
                        ) : (
                          <div className="flex gap-2">
                            <label className="flex-1">
                              {amountEditable && (
                                <span className="mb-1 block text-xs text-palette-gray-secondary">
                                  Date
                                </span>
                              )}
                              <input
                                type="date"
                                value={edit.date ?? ""}
                                onChange={(e) => setEdit(row, { date: e.target.value })}
                                className="w-full rounded-xl border border-palette-gray-divider bg-palette-gray-subtle px-3.5 py-2.5 text-sm text-palette-gray-primary focus:border-palette-indigo-ring focus:outline-none focus:ring-2 focus:ring-palette-indigo-border transition-colors"
                              />
                            </label>
                            {/* No disabled amount box where amounts cannot be
                              edited: a greyed field invites a click that does
                              nothing. The amount is already stated above. */}
                            {amountEditable && (
                              <label className="w-36">
                                <span className="mb-1 block text-xs text-palette-gray-secondary">
                                  Amount
                                </span>
                                <div className="relative">
                                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-palette-gray-placeholder">
                                    $
                                  </span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    inputMode="decimal"
                                    value={edit.amount ?? ""}
                                    onChange={(e) => setEdit(row, { amount: e.target.value })}
                                    className={`w-full rounded-xl border bg-palette-gray-subtle py-2.5 pl-7 pr-3 text-sm tabular-nums text-palette-gray-primary transition-colors focus:outline-none focus:ring-2 ${
                                      error
                                        ? "border-palette-red-border focus:border-palette-red-border focus:ring-palette-red-border"
                                        : "border-palette-gray-divider focus:border-palette-indigo-ring focus:ring-palette-indigo-border"
                                    }`}
                                  />
                                </div>
                              </label>
                            )}
                          </div>
                        )}
                        <p
                          className={`mt-1 text-xs ${error ? "text-palette-red-text" : "text-palette-gray-placeholder"}`}
                        >
                          {error ??
                            (skipped
                              ? "Removed from the cash-flow grid. The schedule keeps every other date, and this one can be restored from All Payments."
                              : o.kind === "statement"
                                ? "Reschedules this payment on the statement. The plan total is unchanged."
                                : amountEditable
                                  ? "Only this payment changes — the recurring schedule keeps its own date and amount."
                                  : "Only this payment moves — the recurring schedule is unchanged.")}
                        </p>
                        {/* Skipping a WEEKDAY-SPLIT occurrence drops that day's
                            share only — the other days of the week do not grow
                            to absorb it. Correct (the money was not paid), but
                            not obvious from a row that shows one figure. */}
                        {skipped && o.splitOf > 1 && (
                          <p className="mt-1 text-xs text-palette-gray-placeholder">
                            This is one of {o.splitOf} payments this cycle is split across — the
                            others keep their own amounts.
                          </p>
                        )}
                        {skipped && guessedCleared && (
                          <p className="mt-1 text-xs text-palette-amber-text">
                            The matcher reads this as already cleared, though nobody has confirmed
                            it. Skipping removes it from the projection anyway.
                          </p>
                        )}
                        {!skipped && allowSkip && confirmedCleared && (
                          <p className="mt-1 text-xs text-palette-gray-placeholder">
                            Confirmed cleared{o.clearedDate ? ` on ${o.clearedDate}` : ""} — the
                            money has left the account, so this one cannot be skipped.
                          </p>
                        )}
                        {/* Per row, not in the footer: a cell can hold several
                            payments from several different schedules, and a
                            single footer button could not say which series it
                            meant. A statement payment has no schedule behind it
                            at all. */}
                        {onEditSchedule && o.kind !== "statement" && o.scheduleId != null && (
                          <button
                            type="button"
                            onClick={() => onEditSchedule(o)}
                            className="mt-1.5 text-xs font-medium text-palette-indigo-badge transition-colors hover:text-palette-indigo-heading hover:underline"
                          >
                            Edit the whole schedule →
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 border-t border-palette-gray-divider px-6 py-4">
                  <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSave}
                    disabled={saving || hasErrors}
                    className="font-semibold"
                  >
                    {saving
                      ? "Saving…"
                      : anySkipped
                        ? "Save changes"
                        : anyAmountEditable
                          ? "Save payment"
                          : "Move date"}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
